import * as React from 'react';
import slugify from 'slugify';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { Grid } from '@fastly/consistently';
import { getEventMode } from '@components/Event';
import LiveIcon from '@components/LiveIcon';
import { Text } from '@components/Typography';
import { useEventModeContext } from '@hooks/use-event-mode';
import Arrow from '../images/arrow.svg';

const ProfileContainer = styled.div`
  display: block;
  position: relative;
`;

const Button = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: block;
  height: 100%;
  z-index: 20;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-grey-dark);
  }
`;

const OffscreenText = styled.span`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: 1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const avatarVariants = {
  guest: {
    background: 'linear-gradient(120deg, var(--color-green) 60%, var(--color-scale-20) 90%)',
    height: '92px',
    arrowColor: 'var(--color-white)',
  },
  host: {
    background: 'linear-gradient(120deg, var(--color-grey) 60%, #d9d9d9 90%)',
    height: '92px',
    arrowColor: '#23e0a8',
  },
};

type Variants = keyof typeof avatarVariants;

type AvatarProps = {
  variant: Variants;
};

const AvatarOuter = styled.div<AvatarProps>`
  position: relative;
  overflow: hidden;
  // padding-top: ${({ variant }) => (variant === 'guest' ? '24px' : null)};

  .gatsby-image-wrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 100%;
    z-index: 10;
    transform: scale(0.97) translate(-4px, 4px);
    transform-origin: 33% 100%;
    transition: transform 1000ms ease-out;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;

    ${Button}:hover + * &,
    ${Button}:focus + * & {
      transform: scale(1) translate(0, 0);
    }
  }
`;

const AvatarInner = styled.div<AvatarProps>`
  position: relative;
  background: ${({ variant }) => avatarVariants[variant].background};
  background-size: 200% 200%;
  border-radius: 4px;
  height: ${({ variant }) => avatarVariants[variant].height};
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  transition: background-position 400ms ease-out;

  ${Button}:hover + * &,
  ${Button}:focus + * & {
    background-position: 100% 100%;
  }

  [data-icon='arrow'] {
    transform: translateX(-4px) scale(0.95);
    transition: transform 200ms ease-out;
    stroke: ${({ variant }) => avatarVariants[variant].arrowColor};

    ${Button}:hover + * &,
    ${Button}:focus + * & {
      transform: translateX(0) scale(1);
    }
  }
`;

type Props = {
  person: GatsbyTypes.PersonFragment;
  variant: Variants;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
};

const Profile = ({ person, variant = 'guest', handleModal }: Props): JSX.Element => {
  const [{ liveSession }] = useEventModeContext();
  const sessionDate: Date | undefined = person.session?.date ? new Date(person.session.date) : undefined;

  const isLive = liveSession && liveSession === person.session?.id;
  const mode = sessionDate ? getEventMode(sessionDate, isLive) : undefined;

  const image = person.image ? getImage(person.image) : null;
  return (
    <ProfileContainer>
      <Button
        onClick={() => handleModal(person, 'person', mode)}
        data-link-id={slugify(`learn more ${person.name?.toLowerCase()}`)}
      >
        <OffscreenText>Find out more about {person.name}</OffscreenText>
      </Button>

      <Grid gap="8px">
        <AvatarOuter variant={variant}>
          {image && <GatsbyImage image={image} alt={person.name ?? ''} objectFit="contain" />}
          <AvatarInner variant={variant}>
            <Arrow data-icon="arrow" stroke="#23E0A8" />
            {isLive && <LiveIcon />}
          </AvatarInner>
        </AvatarOuter>
        <div>
          <Text as="h6" size="xs" weight="bold">
            {person.name}
          </Text>
          <Text as="p" size="xs" weight="light">
            {person.title}
          </Text>
          <Text as="p" size="xs" weight="bold">
            {person.company}
          </Text>
        </div>
      </Grid>
    </ProfileContainer>
  );
};

export default Profile;
